import _uniqBy from 'lodash/uniqBy';

import {
  ACCOUNT_STATUS_CODE,
  CARD_WATCH_REASON,
  CLOSED_REASON,
  FRAUDULENT_CLOSED_REASONS,
  RESTRICTIVE_TIERED_WATCH_REASONS,
  STATUS_OPTIONS,
  TIERED_WATCH_REASON,
} from 'utilities/accountStatusConstants';

const BYPASS_REASON_CODES = Object.freeze({
  [ACCOUNT_STATUS_CODE.TIERED_WATCH]: [
    TIERED_WATCH_REASON.ADS_II_BYPASS,
    TIERED_WATCH_REASON.SECONDARY_BYPASS,
  ],
  [ACCOUNT_STATUS_CODE.CARD_WATCH]: [CARD_WATCH_REASON.FRAUD_ALERT_BYPASS],
});

const isBypass = (statusCode, reasonCode) =>
  (BYPASS_REASON_CODES[statusCode] || []).includes(reasonCode);

const isTieredWatch = (status) =>
  status.statusCode === ACCOUNT_STATUS_CODE.TIERED_WATCH;

const isFraudTieredWatch = (status) =>
  isTieredWatch(status) &&
  RESTRICTIVE_TIERED_WATCH_REASONS.includes(status.reasonCode);

const isBypassTieredWatch = (status) =>
  isTieredWatch(status) && isBypass(status.statusCode, status.reasonCode);

export const hasGenericTieredWatch = (rawStatuses) =>
  (rawStatuses || [])
    .filter(isTieredWatch)
    .some(
      (status) => !isFraudTieredWatch(status) && !isBypassTieredWatch(status),
    );

const getPillColor = (statusCode, reasonCode) => {
  if (isBypass(statusCode, reasonCode)) {
    return 'green';
  }
  if (isFraudTieredWatch({ statusCode, reasonCode })) {
    return 'red';
  }
  return 'yellow';
};

const STATUS_LABEL_OVERRIDES = Object.freeze({
  [`${ACCOUNT_STATUS_CODE.CLOSED}-${CLOSED_REASON.PAYMENT_FRAUD}`]:
    'CL-V7: Payment Fraud',
});

const getStatusLabel = ({ statusCode, reasonCode, description }) => {
  const fullStatusCode = `${statusCode}-${reasonCode}`;
  const statusOption = STATUS_OPTIONS.find(
    (option) => option.value === statusCode,
  );
  const reasonOption = statusOption?.reasonCodes.find(
    (option) => option.value === reasonCode,
  );
  return (
    reasonOption?.label ||
    STATUS_LABEL_OVERRIDES[fullStatusCode] ||
    (description ? `${fullStatusCode}: ${description}` : fullStatusCode)
  );
};

const findStatus = (rawStatuses, statusCode, reasonCodes) =>
  rawStatuses.find(
    (status) =>
      status.statusCode === statusCode &&
      reasonCodes.includes(status.reasonCode),
  );

const FRAUD_HERO_PILLS = Object.freeze([
  {
    statusCode: ACCOUNT_STATUS_CODE.CLOSED,
    reasonCodes: FRAUDULENT_CLOSED_REASONS,
    prefix: 'Account Closed',
  },
  {
    statusCode: ACCOUNT_STATUS_CODE.TIERED_WATCH,
    reasonCodes: RESTRICTIVE_TIERED_WATCH_REASONS,
    prefix: 'Account Under Review',
  },
]);

export const getFraudHeroStatus = (rawStatuses) => {
  const statuses = rawStatuses || [];
  for (const { statusCode, reasonCodes, prefix } of FRAUD_HERO_PILLS) {
    const match = findStatus(statuses, statusCode, reasonCodes);
    if (match) {
      const fullStatusCode = `${statusCode}-${match.reasonCode}`;
      return {
        fullStatusCode,
        text: `${prefix} - ${getStatusLabel(match)}`,
      };
    }
  }

  return null;
};

export const hasFraudClosure = (rawStatuses) =>
  Boolean(
    findStatus(
      rawStatuses || [],
      ACCOUNT_STATUS_CODE.CLOSED,
      FRAUDULENT_CLOSED_REASONS,
    ),
  );

const hasStatusCodePill = (status) =>
  isFraudTieredWatch(status) ||
  isBypassTieredWatch(status) ||
  status.statusCode === ACCOUNT_STATUS_CODE.CARD_WATCH;

const buildStatusCodePill = (status) => {
  const { statusCode, reasonCode } = status;
  const fullStatusCode = `${statusCode}-${reasonCode}`;
  return {
    key: fullStatusCode,
    fullStatusCode,
    label: getStatusLabel(status),
    color: getPillColor(statusCode, reasonCode),
  };
};

export const getStatusCodePills = (rawStatuses) =>
  _uniqBy(
    (rawStatuses || []).filter(hasStatusCodePill).map(buildStatusCodePill),
    'fullStatusCode',
  );
